<script lang="ts">
	import Image from '#components/image.svelte'
	import Video from '#components/video.svelte'
	import Modal from '#components/modal.svelte'
	import Copy from '#components/copy.svelte'
	import Quotes from '#blocks/quotes.svelte'
	import { linky } from '#library/linky.ts'
	import type { CardLite } from '#library/components.schema.build.ts'

	type Props = Omit<CardLite, 'image' | 'link'> & {
		image?: Partial<CardLite['image']>
		link?: Partial<CardLite['link']>
		featured?: boolean
		noimage?: boolean
	}

	let {
		image,
		title,
		subtitle,
		theme,
		video,
		link,
		modal_suptitle,
		modal_title,
		modal_body,
		modal_image,
		modal_quote,
		featured,
		noimage
	}: Props = $props()

	const is_link = $derived(link?.url)
	const is_modal = $derived(modal_title?.length && modal_body?.content?.[0]?.content)

	let open = $state(false)
	let lastTheme = $state('')
	let currentTheme = $state('')

	const themes = [
		'theme-rose-on-white',
		'theme-white-on-rose',
		'theme-lupton-on-pink',
		'theme-lupton-on-white',
		'theme-lupton-on-pollen',
		'theme-lupton-on-lichen',
		'theme-lupton-on-forget-me-not',
		'theme-pink-on-lupton',
		'theme-pink-on-earth',
		'theme-pink-on-crocus',
		'theme-gimson-on-badley',
		'theme-light-gimson-on-canopy'
	]

	function getRandomTheme() {
		// Filter out the last theme to prevent repetition
		const availableThemes = themes.filter(theme => theme !== lastTheme)

		// If no themes available (shouldn't happen), fall back to all themes
		const themePool = availableThemes.length > 0 ? availableThemes : themes

		const selectedTheme = themePool[Math.floor(Math.random() * themePool.length)]
		lastTheme = selectedTheme
		currentTheme = selectedTheme

		return selectedTheme
	}

	// Initialize the theme
	$effect(() => !currentTheme && getRandomTheme())

	const href = $derived(linky`${link?.url}`)
</script>

<div class="card {theme || currentTheme}" class:featured>
	<svelte:element this={is_link ? 'a' : 'figure'} class="figure" class:featured {href} tabindex="-1">
		{#if video?.m3u8_url}
			<Video {video} portrait />
		{:else if image?.filename}
			<Image src={image.filename} alt={image.alt} focus={image.focus} />
		{:else if !noimage}
			<div class="fallback">
				<svg class="fallback-icon">
					<use xlink:href="/images/vector.svg#icon"></use>
				</svg>
			</div>
		{/if}
	</svelte:element>
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<svelte:element
		this={is_link ? 'a' : is_modal ? 'button' : 'div'}
		class="chin"
		{href}
		onclick={() => is_modal && (open = true)}
	>
		<div class="content">
			<div class="title">{title}</div>
			{#if subtitle}
				<div class="subtitle">{subtitle}</div>
			{/if}
		</div>
		{#if is_link || is_modal}
			<svg class="arrow">
				<use xlink:href="/images/vector.svg#arrow-up-right"></use>
			</svg>
		{/if}
	</svelte:element>
</div>

{#if is_modal}
	<Modal title={modal_suptitle} close={() => (open = false)} {open} split>
		<Copy title={modal_title} size="small" body={modal_body} />
		{#if modal_image?.filename}
			<Image src={modal_image.filename} alt={modal_image.alt} focus={modal_image.focus} />
		{/if}
		<Quotes
			blok={{
				items: [{ title: modal_quote, offset: 'left' }],
				padding_top: '32',
				padding_bottom: '32'
			} as never}
		/>
	</Modal>
{/if}

<style lang="css">
	.card {
		background-color: var(--c-background);
		color: var(--c-foreground);
		flex-shrink: 0;

		&.featured {
			flex-shrink: 1;
			width: 100%;
		}
	}

	.figure {
		display: grid;
		overflow: clip;

		:global {
			.image,
			.video {
				aspect-ratio: 1;
				transition: transform 0.3s ease;
				width: 100%;
			}

			&:hover .image,
			&:hover .video {
				transform: scale(1.1);
			}
		}

		&.featured {
			:global {
				.image,
				.video {
					aspect-ratio: 16/9;
				}
			}
		}

		.fallback {
			aspect-ratio: 1/1;
			background-color: var(--c-foreground);
			color: var(--c-gimson);
			padding: 2rem;
		}

		.fallback-icon {
			rotate: -45deg;
		}
	}

	.chin {
		display: flex;
		align-items: center;
		height: 6rem;
		padding: 0 1.25rem;
		text-align: start;
	}

	.content {
		display: grid;
		flex: 1 1 auto;
		gap: 0.25rem;
	}

	.title {
		font: 500 var(--f-h6);
		letter-spacing: var(--ls-h6);
	}

	.subtitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
	}

	.arrow {
		size: 1.75rem;
	}
</style>
